import { Alert, Flex, Form, Input, message, Typography, Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import { add, formatISO } from 'date-fns';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import { CopyIcon } from '../../../../shared/icons/copy-icon';
import { UploadIcon } from '../../../../shared/icons/upload-icon';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors, mapServerErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppSelector } from '../../../../store/hooks';
import ClientUsersUnitSelect from '../../../client-users/components/client-users-unit-select/client-users-unit-select';
import { useCreateSignRequestMutation } from '../../../sign-requests/api/sign-requests-api';
import { SignRequestDetails } from '../../../sign-requests/models/sign-request-details';
import { SignRequestType } from '../../../sign-requests/models/sign-request-type';
import TemplateSelect from '../../../templates/components/template-select/template-select';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useCreateCustomerSignUrlMutation, useGetCustomerQuery } from '../../api/customers-api';
import { customerFormSendDocumentModalId, customersAlertId } from '../../constants/constants';
import { CustomerFormSendDocumentModel } from '../../models/customer-form-send-document-model';
import { selectActiveCustomerId, setActiveCustomerId } from '../../store/customers-slice';
import styles from './customer-form-send-document-modal.module.css';

type SubmitAction = 'send' | 'generateLink';

const CustomersFormModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['customers', 'common']);
  const [copyClipboardMessageApi, copyClipboardMessage] = message.useMessage();
  const dispatch = useDispatch();
  const activeCustomerId = useAppSelector(selectActiveCustomerId);
  const [form] = Form.useForm<CustomerFormSendDocumentModel>();
  const [file, setFile] = useState<RcFile | undefined>(undefined);
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const [generatedSignUrl, setGeneratedSignUrl] = useState<string | null>(null);
  const [submitAction, setSubmitAction] = useState<SubmitAction>('send');
  const submitActionRef = useRef<SubmitAction>('send');

  const { data: customer } = useGetCustomerQuery(activeCustomerId || '', {
    skip: !activeCustomerId,
  });

  const [createSignRequest, { isLoading: isCreateSignRequestLoading }] = useCreateSignRequestMutation();
  const [createCustomerSignUrl, { isLoading: isCreateCustomerSignUrlLoading }] = useCreateCustomerSignUrlMutation();
  const isLoading = isCreateSignRequestLoading || isCreateCustomerSignUrlLoading;

  const resetState = () => {
    form.resetFields();
    setFile(undefined);
    setIsErrorAlertVisible(false);
    setGeneratedSignUrl(null);
    setSubmitAction('send');
    submitActionRef.current = 'send';
  };

  const closeCustomerFormSendDocument = () => {
    dispatch(closeModal(customerFormSendDocumentModalId));
    dispatch(setActiveCustomerId(null));
    resetState();
  };

  const createDocumentSignRequest = async (
    formData: CustomerFormSendDocumentModel,
  ): Promise<SignRequestDetails | undefined> => {
    setIsErrorAlertVisible(false);
    const { customerFormSendUnit, ...restFormData } = formData;

    if (!customer?.id) {
      return undefined;
    }

    return createSignRequest({
      signRequest: {
        unit: customerFormSendUnit,
        customer: customer.id,
        type: SignRequestType.Customer,
        ...restFormData,
      },
      ...(file && { file }),
    })
      .unwrap()
      .catch(error => {
        handleServerFormErrors(
          form,
          mapServerErrors(error.data?.errors || [], {
            unit: 'customerFormSendUnit',
          }),
        );

        if (form.getFieldsError().every(field => field.errors.length === 0)) {
          setIsErrorAlertVisible(true);
        }

        return undefined;
      });
  };

  const submitForm = async (formData: CustomerFormSendDocumentModel) => {
    if (!customer?.id) {
      return;
    }

    const signRequest = await createDocumentSignRequest(formData);

    if (!signRequest?.id) {
      return;
    }

    const action = submitActionRef.current;

    const signUrlResponse = await createCustomerSignUrl({
      customerId: customer.id,
      linkExpiresAt: formatISO(add(new Date(), { months: 1 })),
      ...(action === 'send' && {
        email: customer.email,
        phone: customer.phone,
      }),
      redirectUrl: '',
      signRequestId: signRequest.id,
    })
      .unwrap()
      .catch(error => {
        handleServerFormErrors(
          form,
          mapServerErrors(error.data?.errors || [], {
            unit: 'customerFormSendUnit',
          }),
        );

        if (form.getFieldsError().every(field => field.errors.length === 0)) {
          setIsErrorAlertVisible(true);
        }

        return undefined;
      });

    if (!signUrlResponse?.signUrl) {
      return;
    }

    if (action === 'generateLink') {
      setGeneratedSignUrl(signUrlResponse.signUrl);
      return;
    }

    closeCustomerFormSendDocument();
    dispatch(
      setAlert({
        id: customersAlertId,
        type: 'success',
        title: t('customerSendDocumentToSignModal.messages.documentSent'),
      }),
    );
  };

  const copyGeneratedLink = async () => {
    if (!generatedSignUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedSignUrl);
      copyClipboardMessageApi.open({
        type: 'success',
        content: t('customerSendDocumentToSignModal.messages.linkCopied'),
      });
    } catch {
      copyClipboardMessageApi.open({
        type: 'error',
        content: t('customerSendDocumentToSignModal.messages.linkCopyFailed'),
      });
    }
  };

  return (
    <Modal
      title={t('customerSendDocumentToSignModal.modalTitle')}
      onCancel={() => !isLoading && closeCustomerFormSendDocument()}
      isLoading={isLoading}
      onOk={() => form.submit()}
      isOpen={isOpen}
      afterClose={afterClose}
      modalProps={{
        footer: generatedSignUrl
          ? [
              <CustomButton
                key="copy"
                type="primary"
                onClick={copyGeneratedLink}
                icon={<CopyIcon style={{ color: 'currentColor', fontSize: 16 }} />}
              >
                {t('action.copy', { ns: 'common' })}
              </CustomButton>,
            ]
          : [
              <CustomButton
                key="generateLink"
                type="darkBlue"
                onClick={() => {
                  submitActionRef.current = 'generateLink';
                  setSubmitAction('generateLink');
                  form.submit();
                }}
                loading={submitAction === 'generateLink' && isLoading}
                disabled={isLoading}
              >
                {t('customerSendDocumentToSignModal.actions.generateLink')}
              </CustomButton>,
              <CustomButton
                key="send"
                type="primary"
                onClick={() => {
                  submitActionRef.current = 'send';
                  setSubmitAction('send');
                  form.submit();
                }}
                loading={submitAction === 'send' && isLoading}
                disabled={isLoading}
              >
                {t('action.send', { ns: 'common' })}
              </CustomButton>,
            ],
      }}
    >
      {copyClipboardMessage}
      {isErrorAlertVisible && (
        <Alert
          className={styles.errorAlert}
          message={t('customerSendDocumentToSignModal.validationErrors.submitError')}
          type="error"
          showIcon
        />
      )}
      {generatedSignUrl ? (
        <div className={styles.generatedLinkContainer}>
          <Typography.Title level={5} className={styles.generatedLinkTitle}>
            {t('customerSendDocumentToSignModal.generatedLink.title')}
          </Typography.Title>
          <Typography.Text className={styles.generatedLinkDescription}>
            {t('customerSendDocumentToSignModal.generatedLink.description')}
          </Typography.Text>
          <Input.TextArea
            className={styles.generatedLinkInput}
            value={generatedSignUrl}
            autoSize={{ minRows: 3, maxRows: 6 }}
            readOnly
          />
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={submitForm}>
          <Form.Item
            name="template"
            label={t('label.template', { ns: 'common' })}
            rules={[
              {
                required: true,
                message: t('customerSendDocumentToSignModal.validationErrors.templateRequired'),
              },
            ]}
          >
            <TemplateSelect />
          </Form.Item>
          <Form.Item name="customerFormSendUnit" label={t('label.unit', { ns: 'common' })}>
            <ClientUsersUnitSelect />
          </Form.Item>
          <Form.Item name="files" valuePropName="files">
            <Upload.Dragger
              maxCount={1}
              onChange={({ fileList }) => setFile(fileList[0]?.originFileObj)}
              onRemove={() => setFile(undefined)}
              beforeUpload={() => false}
              accept=".pdf"
            >
              <Flex gap="small" justify="center">
                <UploadIcon className={styles.uploadIcon} />
                <Typography.Text className={styles.subtitle}>
                  {t('fileUpload.uploadDescriptionOnlyPdf', { ns: 'common' })}
                </Typography.Text>
              </Flex>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

const CustomerFormSendDocumentModal = (): JSX.Element => {
  return <LazyModal modalId={customerFormSendDocumentModalId} Component={CustomersFormModalContent} />;
};

export default CustomerFormSendDocumentModal;
